/**
 * The small pieces every Tasks surface shares — the done circle, a record
 * chip, a due chip, the faces — drawn once so the list, the Board, the
 * phone cards and the Task sheet cannot drift (`docs/activity.md` §8.1,
 * §8.5; Boards 9, 16 C, 19).
 */
import { useMemo, type ReactNode } from 'react'
import { cn } from '../../lib/cn.js'
import { Glyph, type GlyphName } from '../glyphs.js'
import { FaceStack, type FacePerson } from '../faces.js'
import { useActivity } from '../activity-context.js'
import type { DueTone, TaskLink } from './types.js'

/**
 * The done circle (§8.1): amber ring, red when late; done is a solid
 * `accent` disc with a white check. A press ticks or unticks and never opens
 * the row it sits in.
 */
export function DoneCircle({
  done, late = false, size = 18, onToggle, label,
}: {
  done: boolean
  late?: boolean
  size?: 17 | 18 | 20 | 22
  onToggle?: () => void
  label?: string
}) {
  const shape = (
    <span
      aria-hidden
      className={cn('inline-flex shrink-0 items-center justify-center rounded-full',
        done ? 'bg-accent text-white' : cn('border-[1.8px] bg-surface', late ? 'border-danger' : 'border-amber-mark'))}
      style={{ width: size, height: size }}
    >
      {done && <Glyph name="check" size={Math.round(size * 0.62)} stroke={3} />}
    </span>
  )
  if (!onToggle) return shape
  return (
    <button
      type="button"
      aria-label={label ?? (done ? 'Mark not done' : 'Mark done')}
      aria-pressed={done}
      onClick={(event) => { event.stopPropagation(); onToggle() }}
      onPointerDown={(event) => event.stopPropagation()}
      className="inline-flex shrink-0 items-center justify-center rounded-full p-0.5 hover:bg-hover"
    >
      {shape}
    </button>
  )
}

const LINK_GLYPHS: Record<string, GlyphName> = {
  job: 'layers',
  property: 'pin',
  contact: 'user',
  company: 'building',
  account: 'building',
  text: 'bubble',
  email: 'envelope',
  list: 'board',
}

export const linkGlyph = (type: string): GlyphName => LINK_GLYPHS[type] ?? 'link'

/** A record a task is attached to (§8.1's *Attached to*): 20 px, kind glyph and label. */
export function LinkChip({ type, label, className }: { type: string; label: string; className?: string }) {
  return (
    <span className={cn('inline-flex h-5 max-w-full shrink-0 items-center gap-1 whitespace-nowrap rounded-[6px]',
      'border border-line-faint bg-surface-alt px-[7px] text-[11px] font-semibold text-text-body', className)}>
      <Glyph name={linkGlyph(type)} size={11} />
      <span className="truncate">{label}</span>
    </span>
  )
}

/** Up to two chips, then *+n* (§8.1). *nothing attached* when there is nothing. */
export function LinkChips({ links, max = 2 }: { links: readonly TaskLink[]; max?: number }) {
  if (links.length === 0) return <span className="text-[13px] text-text-faint">nothing attached</span>
  const shown = links.slice(0, max)
  const more = links.length - shown.length
  return (
    <span className="flex min-w-0 items-center gap-1 overflow-hidden">
      {shown.map((link) => <LinkChip key={`${link.type}:${link.id}`} type={link.type} label={link.label} />)}
      {more > 0 && <span className="shrink-0 pl-0.5 text-[11.5px] font-semibold text-text-faint">+{more}</span>}
    </span>
  )
}

const DUE_CHIP: Record<DueTone, string> = {
  late: 'bg-danger-bg text-danger',
  today: 'bg-warn-bg text-warn',
  other: 'bg-line-soft text-text-muted',
}

/** A card's due chip (§8.5): red when late, amber today, grey otherwise; a press opens the quick dates. */
export function DueChip({
  words, tone, onClick,
}: { words: string; tone: DueTone; onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void }) {
  const inner = (
    <>
      <Glyph name="calendar" size={11} />
      {words}
    </>
  )
  const look = cn('inline-flex h-[22px] shrink-0 items-center gap-1 whitespace-nowrap rounded-[6px] px-[7px]',
    'text-[11.5px] font-semibold', DUE_CHIP[tone])
  if (!onClick) return <span className={look}>{inner}</span>
  return (
    <button type="button" className={cn(look, 'hover:brightness-95')}
      onPointerDown={(event) => event.stopPropagation()}
      onClick={(event) => { event.stopPropagation(); onClick(event) }}>
      {inner}
    </button>
  )
}

/** A task list on a card, when the Board isn't that list (§8.5). */
export function ListChip({ label }: { label: string }) {
  return (
    <span className="inline-flex h-[22px] max-w-full items-center gap-1 whitespace-nowrap rounded-[6px] border border-line bg-surface px-[7px] text-[11.5px] font-semibold text-text-body">
      <Glyph name="board" size={11} />
      <span className="truncate">{label}</span>
    </span>
  )
}

/** The people Activity knows, by id — the directory first, then whatever else the screen was handed. */
export function usePeopleById(extra?: readonly FacePerson[]): Map<string, FacePerson> {
  const { directory } = useActivity()
  return useMemo(() => {
    const map = new Map<string, FacePerson>()
    for (const person of extra ?? []) map.set(person.id, person)
    for (const person of directory?.people ?? []) {
      map.set(person.id, { id: person.id, name: person.name, initials: person.initials, colour: person.colour })
    }
    return map
  }, [directory, extra])
}

/** *TR* from *Tom Reyes*. */
export function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  return ((parts[0]?.[0] ?? '') + (parts.length > 1 ? parts[parts.length - 1]![0] : '')).toUpperCase()
}

/** A row's `faces` field (`[{id, name, colour}]`) as faces. */
export function rowFaces(value: unknown): FacePerson[] {
  if (!Array.isArray(value)) return []
  return (value as { id: string; name: string; colour: string | null }[])
    .map((face) => ({ id: face.id, name: face.name, colour: face.colour, initials: initialsOf(face.name) }))
}

/** Faces for a list of ids, dropping anybody this person cannot see. */
export function Faces({
  ids, byId, size = 20, max = 3,
}: { ids: readonly string[]; byId: Map<string, FacePerson>; size?: number; max?: number }) {
  const people = ids.map((id) => byId.get(id)).filter((p): p is FacePerson => Boolean(p))
  if (people.length === 0) return null
  return <FaceStack people={people} size={size} max={max} />
}

/** A section heading inside the sheet and the rail: 12px, muted, 600. */
export function FieldHead({ children, right }: { children: ReactNode; right?: ReactNode }) {
  return (
    <div className="mb-1.5 flex items-center text-[12px] font-semibold text-text-muted">
      {children}
      {right && <span className="ml-auto font-normal text-text-faint">{right}</span>}
    </div>
  )
}
